import React, { useEffect, useState } from "react";
import DataGrid from "./DataGrid/DataGrid.tsx";
import { BankReportMappingEnum } from "../../enum/normalization-mapping.enum.ts";
import DatePicker from "../Forms/DatePicker/DatePicker.tsx";
import { SelectCell } from "./InstallmentsDataGrid.tsx";

interface AwaitingPaymentReportDataGridProps {
  policies: any[];
  isLoading: boolean;
  paymentPlaces: { [key: string]: string };
  selectedPolicies: [string, string, string, string][];
  setSelectedPolicies: React.Dispatch<
    React.SetStateAction<[string, string, string, string][]>
  >;
  setPolicies: React.Dispatch<React.SetStateAction<any[]>>;
}

const AwaitingPaymentReportDataGrid: React.FC<
  AwaitingPaymentReportDataGridProps
> = ({
  policies,
  selectedPolicies,
  setSelectedPolicies,
  setPolicies,
  paymentPlaces,
  isLoading,
}) => {
  const [selectAllChecked, setSelectAllChecked] = useState(false);

  useEffect(() => {
    if (policies.length > 0) {
      const allSelected = policies.every((policy) =>
        selectedPolicies.some(
          ([polId, payNum]) =>
            polId === policy[BankReportMappingEnum.POL_ID] &&
            payNum === policy[BankReportMappingEnum.PAY_NUM]
        )
      );
      setSelectAllChecked(allSelected);
    } else {
      setSelectAllChecked(false);
    }
  }, [policies, selectedPolicies]);

  const handleSelectAll = (isChecked: boolean) => {
    setSelectAllChecked(isChecked);
    setSelectedPolicies(
      isChecked
        ? policies.map((policy) => [
            policy[BankReportMappingEnum.POL_ID],
            policy[BankReportMappingEnum.PAY_NUM],
            policy[BankReportMappingEnum.DATE_PAYMENT] || "",
            "", // Default empty paymentPlace
          ])
        : []
    );
  };

  const handleRowSelection = (policy: any, isChecked: boolean) => {
    const polId = policy[BankReportMappingEnum.POL_ID];
    const payNum = policy[BankReportMappingEnum.PAY_NUM];

    setSelectedPolicies((prev) => {
      const updated = [...prev];
      const index = updated.findIndex(
        ([id, num]) => id === polId && num === payNum
      );

      if (isChecked && index === -1) {
        // When selecting, preserve existing paymentPlace if it exists
        const existingEntry = updated.find(
          ([id, num]) => id === polId && num === payNum
        );
        const paymentPlace = existingEntry ? existingEntry[3] : "";
        const entry: [string, string, string, string] = [
          polId,
          payNum,
          policy[BankReportMappingEnum.DATE_PAYMENT] || "",
          paymentPlace, // Preserve payment place instead of resetting to ""
        ];
        updated.push(entry);
      } else if (!isChecked && index !== -1) {
        // When deselecting, just remove the entry
        updated.splice(index, 1);
      }

      return updated;
    });
  };

  const handleDateChange = (
    policyId: string,
    payNum: string,
    newDate: string
  ) => {
    setPolicies((prevPolicies) =>
      prevPolicies.map((policy) =>
        policy[BankReportMappingEnum.POL_ID] === policyId &&
        policy[BankReportMappingEnum.PAY_NUM] === payNum
          ? { ...policy, [BankReportMappingEnum.DATE_PAYMENT]: newDate }
          : policy
      )
    );

    setSelectedPolicies((prev) =>
      prev.map(([id, num, date, payPlace]) =>
        id === policyId && num === payNum
          ? [id, num, newDate, payPlace]
          : [id, num, date, payPlace]
      )
    );
  };

  const handlePaymentPlaceChange = (
    policyId: string,
    payNum: string,
    newPaymentPlace: string
  ) => {
    setPolicies((prevPolicies) =>
      prevPolicies.map((policy) =>
        policy[BankReportMappingEnum.POL_ID] === policyId &&
        policy[BankReportMappingEnum.PAY_NUM] === payNum
          ? { ...policy, paymentPlace: newPaymentPlace }
          : policy
      )
    );

    setSelectedPolicies((prev) => {
      // Find if policy is already selected
      const existingIndex = prev.findIndex(
        ([id, num]) => id === policyId && num === payNum
      );

      if (existingIndex !== -1) {
        // If already selected, just update the payment place
        const updated = [...prev];
        updated[existingIndex] = [
          policyId,
          payNum,
          updated[existingIndex][2],
          newPaymentPlace,
        ];
        return updated;
      } else {
        // If not selected, auto-select the policy with the payment place
        const policy = policies.find(
          (p) =>
            p[BankReportMappingEnum.POL_ID] === policyId &&
            p[BankReportMappingEnum.PAY_NUM] === payNum
        );
        if (policy) {
          return [
            ...prev,
            [
              policyId,
              payNum,
              policy[BankReportMappingEnum.DATE_PAYMENT] || "",
              newPaymentPlace,
            ],
          ];
        }
        return prev;
      }
    });
  };

  const columns = [
    {
      name: "selected",
      header: (
        <div className="flex h-full w-full items-center justify-center">
          <input
            type="checkbox"
            checked={selectAllChecked}
            onChange={(e) => handleSelectAll(e.target.checked)}
          />
        </div>
      ),
      render: ({ data }) => (
        <div className="flex h-full w-full items-center justify-center">
          <input
            type="checkbox"
            checked={selectedPolicies.some(
              ([polId, payNum]) =>
                polId === data[BankReportMappingEnum.POL_ID] &&
                payNum === data[BankReportMappingEnum.PAY_NUM]
            )}
            onChange={(e) => handleRowSelection(data, e.target.checked)}
          />
        </div>
      ),
      minWidth: 50,
      maxWidth: 50,
      type: "checkbox",
      sortable: false,
      filterable: false,
      enableColumnFilterContextMenu: false,
    },
    {
      name: BankReportMappingEnum.DATE_PAYMENT,
      render: ({ data }) => {
        return (
          <DatePicker
            name="noPaidReportDate"
            classNameAdditional="border-none pl-0"
            value={data[BankReportMappingEnum.DATE_PAYMENT] || ""}
            onChange={(event) =>
              handleDateChange(
                data[BankReportMappingEnum.POL_ID],
                data[BankReportMappingEnum.PAY_NUM],
                event.target.value
              )
            }
          />
        );
      },
      header: "Дата на превод",
      minWidth: 150,
      type: "string",
    },
    {
      name: "paymentPlace",
      render: ({ data }) => (
        <SelectCell
          value={data.paymentPlace || ""}
          options={Object.entries(paymentPlaces)}
          onChange={(newVal) =>
            handlePaymentPlaceChange(
              data[BankReportMappingEnum.POL_ID],
              data[BankReportMappingEnum.PAY_NUM],
              newVal
            )
          }
        />
      ),
      header: "Място на плащане",
      minWidth: 180,
      type: "string",
    },
    {
      name: BankReportMappingEnum.NAME,
      header: "Име",
      minWidth: 150,
      type: "string",
    },
    {
      name: BankReportMappingEnum.POL_NUM,
      header: "№ Полица",
      minWidth: 140,
      type: "string",
    },
    {
      name: BankReportMappingEnum.INS_COMP,
      header: "ЗК",
      minWidth: 150,
      type: "string",
    },
    {
      name: BankReportMappingEnum.POL_KIND,
      header: "Вид Полица",
      minWidth: 150,
      type: "string",
    },
    {
      name: BankReportMappingEnum.SUMA,
      header: "Сума",
      minWidth: 120,
      type: "string",
    },
    {
      name: BankReportMappingEnum.DATE_ND,
      header: "Дата на Вноска",
      minWidth: 120,
      type: "date",
    },
    {
      name: BankReportMappingEnum.REG_NO,
      header: "ДКН",
      minWidth: 120,
      type: "date",
    },
    {
      name: BankReportMappingEnum.PAY_NUM,
      header: "№ Падеж",
      minWidth: 40,
      type: "date",
    },
  ];

  return (
    <DataGrid
      columns={columns}
      dataSource={policies}
      isLoading={isLoading}
      gridTitle="Отчет на изчакващи плащане"
      idProperty={BankReportMappingEnum.POL_ID}
      csvFilename="policies_no_paid_report.csv"
      onEditComplete={undefined}
    />
  );
};

export default AwaitingPaymentReportDataGrid;
